import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistStorage } from './storage';
import type { UnitSystem } from '../logic/units';
import type { Tradition } from '../logic/holidays';
import type { CalendarId } from '../logic/calendars';
import type { Diet } from '../types';
import { ageRangeOf, type Allergen, type DietProfile } from '../logic/dietPlan';

// Options carried over from the Swift app. Each theme has its own matte palette (theme.ts);
// layout was never read by the Swift app and is kept "as-is" (stored, shown, not used).
export const LANGUAGES = ['system', 'en-US', 'de-DE', 'de-AT', 'de-CH'] as const;
export const THEMES = ['modern', 'mediterranean', 'constructivist', 'asian', 'oriental', 'developer'] as const;
export const LIBRARY_ROOTS = ['list', 'recipes', 'cookbooks'] as const;

export type Settings = {
  displayName: string;
  followedCreators: string[];
  language: (typeof LANGUAGES)[number];
  unitSystem: UnitSystem;
  diets: Diet[];
  restrictDiet: boolean;
  showsMicronutrients: boolean;
  showsAdvancedMicronutrients: boolean;
  /** Body measures and target weight for the diet plan. null: none made (skipping makes none). */
  dietProfile: DietProfile | null;
  allergies: Allergen[];
  /** The diet plan card on the path was opened once: its red dot goes away. */
  dietCardOpened: boolean;
  /** The diet plan questions were skipped (or the plan deleted): nothing on the path; Settings redoes them. */
  dietPlanSkipped: boolean;
  libraryRoot: (typeof LIBRARY_ROOTS)[number];
  theme: (typeof THEMES)[number];
  /** Calendar dates are shown in. Days are stored as Gregorian dates either way. */
  calendar: CalendarId;
  /** Holiday traditions the user keeps (several allowed): suggestions and decorations. */
  traditions: Tradition[];
  /** Holiday decorations and the short animation on holidays. */
  holidayDecor: boolean;
  /** `holiday:day` the animation last played for, so it plays once per holiday day. */
  holidayAnimationPlayed: string;
  layout: 'grid' | 'list';
  /** Show converted amounts in the user's unit system on recipes and in cook mode. */
  convertUnits: boolean;
  cookFontScale: 1 | 1.25 | 1.5;
  /** Accessibility: words next to icons. Off by default, the app speaks in icons (on with a screen reader). */
  showLabels: boolean;
  hapticsEnabled: boolean;
  onboardingSeen: boolean;
  /** Cached store entitlement; re-checked on launch (native). */
  isSuper: boolean;
  /** Last server-verified expiry; never synced to another device. */
  superExpiresAt: number | null;
  updatedAt: number;
};

const DEFAULTS: Settings = {
  displayName: '',
  followedCreators: [],
  language: 'system',
  unitSystem: 'metric',
  diets: [],
  restrictDiet: false,
  showsMicronutrients: true,
  showsAdvancedMicronutrients: false,
  dietProfile: null,
  allergies: [],
  dietCardOpened: false,
  dietPlanSkipped: false,
  libraryRoot: 'list',
  theme: 'modern',
  calendar: 'gregorian',
  traditions: ['seasonal'],
  holidayDecor: true,
  holidayAnimationPlayed: '',
  layout: 'grid',
  convertUnits: false,
  cookFontScale: 1,
  showLabels: false,
  hapticsEnabled: true,
  onboardingSeen: false,
  isSuper: false,
  superExpiresAt: 0,
  updatedAt: 0,
};

type V4NutritionProfile = { enabled: boolean; weightKg: number; ageYears: number; heightCm: number };

/** v4 → v5: "Override daily optimum" became the diet profile (no goal: target = current weight). */
function profileFromV4(old?: V4NutritionProfile): DietProfile | null {
  return old?.enabled ? { weightKg: old.weightKg, heightCm: old.heightCm, age: ageRangeOf(old.ageYears), targetWeightKg: old.weightKg } : null;
}

export function migrateSettings(persisted: unknown, version: number): Settings {
  // v5 → v6: the activity level (a factor on the resting rate) gave way to steps and sport
  // minutes. It cannot be turned into either, so it is dropped: the plan counts default steps.
  if (version === 5) {
    const s = persisted as Settings;
    if (!s.dietProfile) return { ...DEFAULTS, ...s };
    const { activity: _dropped, ...profile } = s.dietProfile as DietProfile & { activity?: string };
    return { ...DEFAULTS, ...s, dietProfile: profile };
  }
  // v3 → v4: the Latin theme became Constructivist. v4 → v5: the nutrition profile became the diet profile.
  if (version >= 3 && version <= 4) {
    const { nutritionProfile, ...s } = persisted as Omit<Settings, 'theme'> & { theme: string; nutritionProfile?: V4NutritionProfile };
    const theme = (s.theme === 'latin' ? 'constructivist' : s.theme) as Settings['theme'];
    return { ...DEFAULTS, ...s, theme, dietProfile: profileFromV4(nutritionProfile) };
  }
  // v2 → v3: one holiday tradition ('none' or a tradition) became a list.
  if (version === 2) {
    const { holidays, nutritionProfile, ...rest } = persisted as Omit<Settings, 'traditions' | 'holidayDecor' | 'holidayAnimationPlayed'> & { holidays?: string; nutritionProfile?: V4NutritionProfile };
    const theme = (rest.theme as string) === 'latin' ? 'constructivist' : rest.theme;
    return { ...DEFAULTS, ...rest, theme, traditions: !holidays || holidays === 'none' ? [] : [holidays as Tradition], dietProfile: profileFromV4(nutritionProfile) };
  }
  // v1 was the step-0 spike (only a counter): start from defaults.
  return DEFAULTS;
}

type SettingsStore = Settings & { set: (patch: Partial<Settings>) => void };

export const useSettings = create<SettingsStore>()(
  persist(
    set => ({
      ...DEFAULTS,
      set: patch => set({ ...patch, updatedAt: Date.now() }),
    }),
    {
      name: 'settings',
      storage: persistStorage,
      version: 6,
      migrate: migrateSettings,
    },
  ),
);

/** "Developer" theme unlocks developer modes, exactly like the Swift app. */
export const useDeveloperMode = () => useSettings(s => s.theme === 'developer');
